import { emptyQuery } from 'hooks/usePaginatedIcon/usePaginatedIconSearch/usePaginatedIconSearch.config';
import { useCallback, useMemo, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import useSWRInfinite from 'swr/infinite';

import { IconSearchData } from '../common/usePaginatedIcon.types';
import { FETCH_FN_MAP, checkHasMore, generateKey, parseIcons } from './usePaginatedIconPack.helpers';
import {
  IconPackResponseTypeMap,
  IconPackState,
  PaginatedIconPack,
  UsePaginatedIconPackOptions,
} from './usePaginatedIconPack.types';

/**
 * Hook to fetch and paginate icon pack data
 * Used to fetch icons from Algolia and Icons8, and Icons8 illustrations as defined by the `type` option.
 *
 * @param options: { type: 'algolia' | 'icons8' | 'icons8Illustration' } type of pack
 * @returns
 */
export const usePaginatedIconPack = (options: UsePaginatedIconPackOptions): PaginatedIconPack => {
  const { type } = options;
  const [iconPackData, setIconPackData] = useState<IconPackState | null>(null);
  const token = useLoaderData() as string;

  const fetchFn = useMemo(() => FETCH_FN_MAP[type], [type]);
  const fetcher = useCallback((key: string) => fetchFn(key, token), [fetchFn, token]);
  const getKey = useCallback((page: number) => generateKey(page, iconPackData!, type), [iconPackData, type]);

  const swrConfig = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateFirstPage: false,

    // Skip fetch if no icon pack data
    fallbackData: iconPackData ? undefined : [emptyQuery],
  };

  const {
    data,
    error,
    size: page,
    setSize: setPage,
  } = useSWRInfinite<IconPackResponseTypeMap[typeof type]>(getKey, iconPackData ? fetcher : null, swrConfig);

  const icons: IconSearchData[] = useMemo(() => parseIcons(type, data), [type, data]);
  const onShowMore = useCallback(() => setPage(page + 1), [setPage, page]);
  const resetIconPack = useCallback(() => {
    setIconPackData(null);
    setPage(1);
  }, [setPage]);

  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (page > 0 && !!data && typeof data[page - 1] === 'undefined');
  const hasNoResultsFound = !!(icons.length === 0 && !isLoadingInitialData && !!iconPackData);
  const hasMore = !!data && !!data.length && checkHasMore(type, page, data[data.length - 1]);

  return {
    setIconPackData,
    resetIconPack,
    error,
    hasMore,
    isLoadingMore,
    isLoadingInitialData,
    icons: !isLoadingInitialData ? icons : [],
    onShowMore,
    hasNoResultsFound,
  };
};
